/**
 * Work Board — canonical Kanban projection over existing Brunel state.
 *
 * Another operational view of Roadmap, Planner, tasks, dispatch/run, review and
 * approval rows. NOT a second task database. Cards move only when those sources
 * change. Missing fields stay Unknown — never invent branch/PR/host/etc.
 *
 * Exact column mapping (also in docs/WORK-BOARD.md):
 *
 * BLOCKED  ← roadmap.blocked | task.blocked | command_plan.failed
 *            | terminal waiting_for_input
 * REVIEW   ← task.awaiting_approval | task.verifying | plan.proposed
 *            | command_plan.proposed+requires_approval | command_plan.evaluating
 *            | command_plan.needs_owner | terminal waiting_for_approval
 *            | pending Approval row
 * RUNNING  ← running_tasks row | task.working | command_plan.sent
 * PLANNED  ← roadmap.in_progress | plan.approved | command_plan.drafting
 *            | command_plan.proposed without approval gate
 * READY    ← roadmap.open+current | task.waiting
 * DONE     ← roadmap.done | task.completed|failed|discarded | finished_runs
 *            | command_plan.completed|withdrawn
 * BACKLOG / ROADMAP ← roadmap.open + horizon next|later
 *
 * Merge precedence when multiple sources share an identity:
 * running > terminal > approval > task > command_plan > plan > finished > roadmap
 *
 * Owner actions route through Brunel's existing guarded transitions and exact
 * resolution surfaces. Work Board never owns a second mutation path.
 */

import type {
  Approval,
  CommandPlanRow,
  FinishedRun,
  MissionTask,
  NodeCapacityRow,
  Plan,
  Project,
  RoadmapItemRow,
  RunningTask,
  WaitingJobRecoveryRow,
  Worker,
  WorkerTerminalRow,
} from './data';
import { parseOfferingKey } from './offering-resolve';
import { wasAnswered } from './plans';
import { runningFor } from './runs';

export type WorkBoardColumnId =
  | 'backlog_roadmap'
  | 'ready'
  | 'planned'
  | 'running'
  | 'review'
  | 'blocked'
  | 'done';

export const WORK_BOARD_COLUMNS: readonly {
  readonly id: WorkBoardColumnId;
  readonly label: string;
  readonly answers: string;
}[] = [
  { id: 'backlog_roadmap', label: 'BACKLOG / ROADMAP', answers: 'What is waiting on the roadmap' },
  { id: 'ready', label: 'READY', answers: 'What is ready to start' },
  { id: 'planned', label: 'PLANNED', answers: 'What is planned' },
  { id: 'running', label: 'RUNNING', answers: 'What is running / who' },
  { id: 'review', label: 'REVIEW', answers: 'What needs review' },
  { id: 'blocked', label: 'BLOCKED', answers: 'What is blocked' },
  { id: 'done', label: 'DONE', answers: 'What is finished' },
] as const;

/** Honest stand-in when a real field is absent. */
export const WORK_BOARD_UNKNOWN = 'Unknown';

export type WorkBoardSourceKind =
  | 'roadmap'
  | 'plan'
  | 'task'
  | 'running'
  | 'finished'
  | 'command_plan'
  | 'approval'
  | 'terminal';

export interface WorkBoardCard {
  readonly id: string;
  readonly column: WorkBoardColumnId;
  readonly title: string;
  readonly refId: string | null;
  readonly projectId?: string | null;
  readonly projectName: string | null;
  readonly workerLabel: string | null;
  readonly workerId: string | null;
  readonly runtimeModel: string | null;
  readonly executionHost: string | null;
  readonly branch: string | null;
  readonly pullRequest: string | null;
  readonly currentState: string;
  readonly elapsed: string | null;
  readonly blockerOrApproval: string | null;
  readonly testOrReview: string | null;
  readonly usageWarning: string | null;
  readonly sourceKind: WorkBoardSourceKind;
  readonly taskId: string | null;
  /** Canonical ordering evidence. Null means the source does not expose a timestamp. */
  readonly sortAt?: string | null;
}

export interface WorkBoardInput {
  readonly orgId: string;
  readonly roadmap: readonly RoadmapItemRow[];
  readonly plans: readonly Plan[];
  readonly missionTasks: readonly MissionTask[];
  readonly running: readonly RunningTask[];
  readonly results: readonly FinishedRun[];
  readonly commandPlans: readonly CommandPlanRow[];
  readonly workerTerminals: readonly WorkerTerminalRow[];
  readonly approvals: readonly Approval[];
  readonly workers: readonly Worker[];
  readonly projects: readonly Project[];
  readonly capacity: readonly NodeCapacityRow[];
  readonly nowMs: number;
  /** Jobs Brunel closed without running them, with the one line saying why. Optional: none is none. */
  readonly jobRecoveries?: readonly WaitingJobRecoveryRow[];
}

export interface WorkBoardModel {
  readonly columns: readonly {
    readonly id: WorkBoardColumnId;
    readonly label: string;
    readonly answers: string;
    readonly cards: readonly WorkBoardCard[];
  }[];
  readonly totalCards: number;
}

const SOURCE_RANK: Readonly<Record<WorkBoardSourceKind, number>> = {
  running: 80,
  terminal: 70,
  approval: 65,
  task: 50,
  command_plan: 40,
  plan: 30,
  finished: 20,
  roadmap: 10,
};

function text(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? null : trimmed;
}

function projectNameOf(projects: readonly Project[], projectId: string | null | undefined): string | null {
  if (projectId == null) return null;
  return projects.find((p) => p.id === projectId)?.name ?? null;
}

function workerLabelOf(
  workers: readonly Worker[],
  workerId: string | null | undefined,
  workerNumber: number | null | undefined,
  workerName: string | null | undefined,
): string | null {
  if (workerId != null) {
    const known = workers.find((w) => w.id === workerId);
    if (known) return `Worker ${known.number} · ${known.name}`;
  }
  if (workerNumber != null) {
    const name = text(workerName);
    return name !== null ? `Worker ${workerNumber} · ${name}` : `Worker ${workerNumber}`;
  }
  return text(workerName);
}

function runtimeFromOffering(offeringKey: string | null | undefined): string | null {
  const parsed = parseOfferingKey(offeringKey);
  if (parsed.runtimeKey === null && parsed.modelKey === null) return null;
  if (parsed.runtimeKey !== null && parsed.modelKey !== null) {
    return `${parsed.runtimeKey} · ${parsed.modelKey}`;
  }
  return parsed.runtimeKey ?? parsed.modelKey;
}

function capacityWarning(
  capacity: readonly NodeCapacityRow[],
  nodeId: string | null | undefined,
  nodeName: string | null | undefined,
): string | null {
  if (nodeId != null) {
    const held = text(capacity.find((c) => c.node_id === nodeId)?.holding_back ?? null);
    if (held !== null) return held;
  }
  if (nodeName != null) {
    const held = text(capacity.find((c) => c.name === nodeName)?.holding_back ?? null);
    if (held !== null) return held;
  }
  return null;
}

/** Roadmap → column. `dropped` is omitted (null). */
export function mapRoadmapToColumn(
  status: RoadmapItemRow['status'],
  horizon: RoadmapItemRow['horizon'],
): WorkBoardColumnId | null {
  switch (status) {
    case 'dropped': return null;
    case 'blocked': return 'blocked';
    case 'done': return 'done';
    case 'in_progress': return 'planned';
    case 'open': return horizon === 'current' ? 'ready' : 'backlog_roadmap';
    default: return null;
  }
}

/** Mission task status → column. Unknown status → null (omit rather than guess). */
export function mapTaskStatusToColumn(status: string): WorkBoardColumnId | null {
  switch (status) {
    case 'blocked': return 'blocked';
    case 'awaiting_approval':
    case 'verifying': return 'review';
    case 'working': return 'running';
    case 'waiting': return 'ready';
    case 'completed':
    case 'failed':
    case 'discarded': return 'done';
    default: return null;
  }
}

/** Owner plan → column. Answered/rejected omitted. */
export function mapPlanStatusToColumn(status: Plan['status'], answered: boolean): WorkBoardColumnId | null {
  if (answered) return null;
  switch (status) {
    case 'proposed': return 'review';
    case 'approved': return 'planned';
    case 'rejected': return null;
    case 'failed': return 'blocked';
    default: return null;
  }
}

/** Command plan (planner run) → column. */
export function mapCommandPlanStatusToColumn(
  status: string,
  requiresApproval: boolean,
): WorkBoardColumnId | null {
  switch (status) {
    case 'failed': return 'blocked';
    case 'needs_owner':
    case 'evaluating': return 'review';
    case 'proposed': return requiresApproval ? 'review' : 'planned';
    case 'sent': return 'running';
    case 'drafting': return 'planned';
    case 'completed':
    case 'withdrawn': return 'done';
    default: return null;
  }
}

/** Terminal wait states that force REVIEW / BLOCKED. */
export function mapTerminalStatusToColumn(terminalStatus: string | null): WorkBoardColumnId | null {
  if (terminalStatus === 'waiting_for_approval') return 'review';
  if (terminalStatus === 'waiting_for_input') return 'blocked';
  return null;
}

function cardKey(kind: WorkBoardSourceKind, id: string): string {
  return `${kind}:${id}`;
}

function taskIdentity(projectId: string | null | undefined, taskId: string): string {
  return `task:${projectId ?? 'unknown-project'}:${taskId}`;
}

function inOrg(orgId: string, row: { readonly org_id: string }): boolean {
  return row.org_id === orgId;
}

function mergeCard(existing: WorkBoardCard, next: WorkBoardCard): WorkBoardCard {
  const nextSourceWins = SOURCE_RANK[next.sourceKind] >= SOURCE_RANK[existing.sourceKind];
  // The authoritative source owns state/column. Lower-authority rows may only
  // enrich missing descriptive fields; they cannot move live work backward.
  const column = nextSourceWins ? next.column : existing.column;
  const pick = <T>(a: T | null | undefined, b: T | null | undefined): T | null =>
    (nextSourceWins ? a : b) ?? (nextSourceWins ? b : a) ?? null;
  return {
    id: nextSourceWins ? next.id : existing.id,
    column,
    title: nextSourceWins ? next.title : existing.title,
    refId: pick(next.refId, existing.refId),
    projectId: pick(next.projectId, existing.projectId),
    projectName: pick(next.projectName, existing.projectName),
    workerLabel: pick(next.workerLabel, existing.workerLabel),
    workerId: pick(next.workerId, existing.workerId),
    runtimeModel: pick(next.runtimeModel, existing.runtimeModel),
    executionHost: pick(next.executionHost, existing.executionHost),
    branch: pick(next.branch, existing.branch),
    pullRequest: pick(next.pullRequest, existing.pullRequest),
    currentState: nextSourceWins ? next.currentState : existing.currentState,
    elapsed: pick(next.elapsed, existing.elapsed),
    blockerOrApproval: pick(next.blockerOrApproval, existing.blockerOrApproval),
    testOrReview: pick(next.testOrReview, existing.testOrReview),
    usageWarning: pick(next.usageWarning, existing.usageWarning),
    sourceKind: nextSourceWins ? next.sourceKind : existing.sourceKind,
    taskId: pick(next.taskId, existing.taskId),
    sortAt: pick(next.sortAt, existing.sortAt),
  };
}

/**
 * Build the Work Board projection from existing Command reads.
 * Identity prefers task id, else plan/command-plan/roadmap/terminal/approval id.
 */
export function buildWorkBoard(input: WorkBoardInput): WorkBoardModel {
  const byIdentity = new Map<string, WorkBoardCard>();
  const sourceOf = new Map<string, WorkBoardSourceKind>();

  const put = (identity: string, card: WorkBoardCard) => {
    const prev = byIdentity.get(identity);
    const prevSource = sourceOf.get(identity);
    if (prev === undefined || prevSource === undefined) {
      byIdentity.set(identity, card);
      sourceOf.set(identity, card.sourceKind);
      return;
    }
    byIdentity.set(identity, mergeCard(prev, card));
    if (SOURCE_RANK[card.sourceKind] >= SOURCE_RANK[prevSource]) {
      sourceOf.set(identity, card.sourceKind);
    }
  };

  const workers = input.workers.filter((row) => inOrg(input.orgId, row));
  const projects = input.projects.filter((row) => inOrg(input.orgId, row));
  const capacity = input.capacity.filter((row) => inOrg(input.orgId, row));
  const workerByNumber = new Map(workers.map((w) => [w.number, w]));

  for (const run of input.running.filter((row) => inOrg(input.orgId, row))) {
    const worker = run.worker_number != null ? workerByNumber.get(run.worker_number) : undefined;
    const terminal = worker != null
      ? input.workerTerminals.find((t) => inOrg(input.orgId, t)
        && t.worker_id === worker.id && t.project_id === run.project_id)
      : undefined;
    put(taskIdentity(run.project_id, run.task_id), {
      id: cardKey('running', run.assignment_id),
      column: 'running',
      title: text(run.title) ?? WORK_BOARD_UNKNOWN,
      refId: run.task_id,
      projectId: run.project_id,
      projectName: text(run.project_name) ?? projectNameOf(projects, run.project_id),
      workerLabel: workerLabelOf(workers, worker?.id ?? null, run.worker_number, run.worker_name),
      workerId: worker?.id ?? null,
      runtimeModel: runtimeFromOffering(terminal?.offering_key),
      executionHost: text(run.node_name),
      branch: null,
      pullRequest: null,
      currentState: 'running',
      elapsed: runningFor(run.started_at, input.nowMs),
      blockerOrApproval: null,
      testOrReview: null,
      usageWarning: capacityWarning(capacity, terminal?.node_id ?? null, run.node_name),
      sourceKind: 'running',
      taskId: run.task_id,
      sortAt: run.started_at,
    });
  }

  for (const row of input.workerTerminals.filter((item) => inOrg(input.orgId, item))) {
    const terminalColumn = mapTerminalStatusToColumn(row.terminal_status);
    if (terminalColumn === null) continue;
    const identity = row.task_id != null ? taskIdentity(row.project_id, row.task_id) : `terminal:${row.worker_id}`;
    const pending = input.approvals.filter((a) => inOrg(input.orgId, a) && a.action.workerId === row.worker_id).length;
    put(identity, {
      id: cardKey('terminal', row.worker_id),
      column: terminalColumn,
      title: text(row.task_title) ?? text(row.worker_name) ?? `Worker ${row.worker_number}`,
      refId: row.task_id,
      projectId: row.project_id,
      projectName: text(row.project_name) ?? projectNameOf(projects, row.project_id),
      workerLabel: workerLabelOf(workers, row.worker_id, row.worker_number, row.worker_name),
      workerId: row.worker_id,
      runtimeModel: runtimeFromOffering(row.offering_key),
      executionHost: text(row.node_name),
      branch: null,
      pullRequest: null,
      currentState: row.terminal_status ?? row.worker_status,
      elapsed: row.task_started_at != null ? runningFor(row.task_started_at, input.nowMs) : null,
      blockerOrApproval: terminalColumn === 'review'
        ? (pending > 0 ? `Approval pending (${pending})` : 'Waiting for approval')
        : 'Waiting for input',
      testOrReview: terminalColumn === 'review' ? 'approval' : null,
      usageWarning: capacityWarning(capacity, row.node_id, row.node_name),
      sourceKind: 'terminal',
      taskId: row.task_id,
      sortAt: row.task_started_at,
    });
  }

  for (const approval of input.approvals.filter((row) => inOrg(input.orgId, row))) {
    const workerId = typeof approval.action.workerId === 'string' ? approval.action.workerId : null;
    const taskId = typeof approval.action.taskId === 'string' ? approval.action.taskId : null;
    const identity = taskId != null
      ? taskIdentity(approval.project_id, taskId)
      : `approval:${approval.id}`;
    const kind = typeof approval.action.kind === 'string' ? approval.action.kind : 'approval';
    put(identity, {
      id: cardKey('approval', approval.id),
      column: 'review',
      title: kind,
      refId: approval.id,
      projectId: approval.project_id,
      projectName: projectNameOf(projects, approval.project_id),
      workerLabel: workerLabelOf(workers, workerId, null, null),
      workerId,
      runtimeModel: null,
      executionHost: null,
      branch: null,
      pullRequest: null,
      currentState: 'awaiting_approval',
      elapsed: null,
      blockerOrApproval: 'Approval pending',
      testOrReview: 'approval',
      usageWarning: null,
      sourceKind: 'approval',
      taskId,
      sortAt: approval.requested_at ?? null,
    });
  }

  for (const task of input.missionTasks.filter((row) => inOrg(input.orgId, row))) {
    const column = mapTaskStatusToColumn(task.status);
    if (column === null) continue;
    // Closed without a run: done elsewhere, or expired. The card says which, and why.
    const recovery = (input.jobRecoveries ?? []).find((row) => inOrg(input.orgId, row)
      && row.task_id === task.id && row.project_id === task.project_id
      && ((row.outcome === 'done_elsewhere' && task.status === 'completed')
        || (row.outcome === 'expired' && task.status === 'discarded')));
    put(taskIdentity(task.project_id, task.id), {
      id: cardKey('task', task.id),
      column,
      title: text(task.title) ?? WORK_BOARD_UNKNOWN,
      refId: task.id,
      projectId: task.project_id,
      projectName: projectNameOf(projects, task.project_id),
      workerLabel: null,
      workerId: null,
      runtimeModel: null,
      executionHost: null,
      branch: null,
      pullRequest: null,
      currentState: recovery === undefined ? task.status : recovery.outcome === 'expired' ? 'expired' : 'done elsewhere',
      elapsed: null,
      blockerOrApproval: recovery !== undefined ? text(recovery.reason)
        : task.status === 'blocked' ? 'blocked'
        : task.status === 'awaiting_approval' ? 'Approval pending' : null,
      testOrReview: task.status === 'verifying' ? 'verifying'
        : task.status === 'awaiting_approval' ? 'approval' : null,
      usageWarning: null,
      sourceKind: 'task',
      taskId: task.id,
      sortAt: null,
    });
  }

  for (const plan of input.commandPlans.filter((row) => inOrg(input.orgId, row))) {
    const column = mapCommandPlanStatusToColumn(plan.status, plan.requires_approval);
    if (column === null) continue;
    put(`command_plan:${plan.id}`, {
      id: cardKey('command_plan', plan.id),
      column,
      title: text(plan.goal) ?? WORK_BOARD_UNKNOWN,
      refId: plan.id,
      projectId: null,
      projectName: null,
      workerLabel: workerLabelOf(workers, plan.worker_id, plan.worker_number, plan.worker_name),
      workerId: plan.worker_id,
      runtimeModel: text(plan.planner_key),
      executionHost: text(plan.node_name),
      branch: null,
      pullRequest: null,
      currentState: plan.status,
      elapsed: plan.sent_at != null ? runningFor(plan.sent_at, input.nowMs) : null,
      blockerOrApproval: plan.status === 'needs_owner' || (plan.status === 'proposed' && plan.requires_approval)
        ? 'Needs owner'
        : text(plan.failure),
      testOrReview: plan.status === 'evaluating' ? 'evaluating' : plan.verdict,
      usageWarning: capacityWarning(capacity, plan.node_id, plan.node_name),
      sourceKind: 'command_plan',
      taskId: null,
      sortAt: plan.sent_at ?? plan.created_at,
    });
  }

  for (const plan of input.plans.filter((row) => inOrg(input.orgId, row))) {
    const column = mapPlanStatusToColumn(plan.status, wasAnswered(plan));
    if (column === null) continue;
    const identity = plan.mission_id != null ? `mission:${plan.mission_id}` : `plan:${plan.id}`;
    put(identity, {
      id: cardKey('plan', plan.id),
      column,
      title: text(plan.summary) ?? WORK_BOARD_UNKNOWN,
      refId: plan.id,
      projectId: plan.project_id,
      projectName: projectNameOf(projects, plan.project_id),
      workerLabel: null,
      workerId: null,
      runtimeModel: null,
      executionHost: null,
      branch: null,
      pullRequest: null,
      currentState: plan.status,
      elapsed: null,
      blockerOrApproval: plan.status === 'proposed' ? 'Plan awaiting approval' : text(plan.reason),
      testOrReview: plan.status === 'proposed' ? 'plan review' : null,
      usageWarning: null,
      sourceKind: 'plan',
      taskId: null,
      sortAt: plan.created_at,
    });
  }

  // Newest first, so only a task's latest run is joined. A higher-authority card (the task
  // row itself) keeps its state and column; the run only fills the execution evidence it
  // lacks - Worker, runtime, the run's own result and, for a run that did not succeed, why -
  // instead of leaving those fields Unknown (#644). A finished session is not a task outcome.
  const joinedRuns = new Set<string>();
  for (const run of input.results.filter((row) => inOrg(input.orgId, row))) {
    const identity = taskIdentity(run.project_id, run.task_id);
    if (joinedRuns.has(identity)) continue;
    joinedRuns.add(identity);
    // Live work (running / terminal / approval) is described by its own rows, never an older run.
    const existingSource = sourceOf.get(identity);
    if (existingSource !== undefined && SOURCE_RANK[existingSource] > SOURCE_RANK.task) continue;
    put(identity, {
      id: cardKey('finished', run.assignment_id),
      column: 'done',
      title: text(run.summary) ?? WORK_BOARD_UNKNOWN,
      refId: run.task_id,
      projectId: run.project_id,
      projectName: projectNameOf(projects, run.project_id),
      workerLabel: workerLabelOf(workers, run.worker_id, run.worker_number, null),
      workerId: run.worker_id,
      runtimeModel: run.runtime_key != null
        ? (run.model_key != null ? `${run.runtime_key} · ${run.model_key}` : run.runtime_key)
        : null,
      executionHost: null,
      branch: null,
      pullRequest: null,
      currentState: run.success ? 'succeeded' : 'failed',
      elapsed: null,
      blockerOrApproval: run.success ? null : text(run.summary),
      testOrReview: run.success ? 'run succeeded' : 'run failed',
      usageWarning: null,
      sourceKind: 'finished',
      taskId: run.task_id,
      sortAt: run.reported_at,
    });
  }

  for (const item of input.roadmap.filter((row) => inOrg(input.orgId, row))) {
    const column = mapRoadmapToColumn(item.status, item.horizon);
    if (column === null) continue;
    put(`roadmap:${item.id}`, {
      id: cardKey('roadmap', item.id),
      column,
      title: text(item.title) ?? WORK_BOARD_UNKNOWN,
      refId: item.id,
      projectId: item.project_id,
      projectName: projectNameOf(projects, item.project_id),
      workerLabel: text(item.owner_label),
      workerId: null,
      runtimeModel: null,
      executionHost: null,
      branch: null,
      pullRequest: null,
      currentState: `${item.status} · ${item.horizon}`,
      elapsed: null,
      blockerOrApproval: item.status === 'blocked' ? 'blocked' : null,
      testOrReview: null,
      usageWarning: null,
      sourceKind: 'roadmap',
      taskId: null,
      sortAt: item.updated_at,
    });
  }

  const all = [...byIdentity.values()].sort((left, right) => {
    const leftAt = left.sortAt ?? null;
    const rightAt = right.sortAt ?? null;
    if (leftAt !== rightAt) {
      if (leftAt === null) return 1;
      if (rightAt === null) return -1;
      return rightAt.localeCompare(leftAt);
    }
    return left.title.localeCompare(right.title);
  });
  return {
    columns: WORK_BOARD_COLUMNS.map((col) => ({
      id: col.id,
      label: col.label,
      answers: col.answers,
      cards: all.filter((c) => c.column === col.id),
    })),
    totalCards: all.length,
  };
}

/** Display helper: real value or Unknown — never invent. */
export function workBoardField(value: string | null | undefined): string {
  return text(value) ?? WORK_BOARD_UNKNOWN;
}
